import React, { useCallback, useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ProductRow } from "../../../../services/v2/inventory.service";
import { Loading, apiError } from "../kits/shared";
import { Seleccionado, VentanaTienda } from "./piezasTienda";
import ProductPicker from "./productPicker";

/*
	Unificar productos repetidos (Esteban, 2026-11-03), solo Super Admin. Se marcan con los cuadritos en Productos o en
	Bodegas y se abre esta ventana: se elige cuál se queda, se ve qué pasará con cada uno y se escribe el motivo.
	Los repetidos quedan ocultos con su historial; un solo Deshacer los regresa todos.
*/

const COMPOSITE = ["kit", "uniform"];
const SIMPLE_KINDS = ["article", "vitrina", "academic", "service", "digital"];

interface VentanaProps {
	campusId: number;
	/** Abierta desde la ficha: va encima de ella. */
	sobre?: boolean;
	/** Lo marcado con los cuadritos. */
	iniciales: Seleccionado[];
	onClose: () => void;
	/** survivorId: el producto que se quedó. */
	onDone: (message: string, survivorId: number) => void;
}

export const UnificarVentana: React.FC<VentanaProps> = ({ campusId, sobre, iniciales, onClose, onDone }) => {
	const http = HttpManager.getInstance().inventoryService;
	const [members, setMembers] = useState<Seleccionado[]>(iniciales);
	// Se queda, de entrada, el que tiene más piezas (o el más antiguo).
	const [survivorId, setSurvivorId] = useState<number>(() => [...iniciales].sort((a, b) => Number(b.onHand || 0) - Number(a.onHand || 0) || a.id - b.id)[0]?.id || 0);
	/** Tallas del que se queda: un repetido sin tallas se une a una de ellas. */
	const [survivorVariants, setSurvivorVariants] = useState<any[]>([]);
	const [variantFor, setVariantFor] = useState<Record<number, string>>({});
	const [preview, setPreview] = useState<any | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [reason, setReason] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	/** Tocó Unificar sin que se pudiera: se marca lo que falta. */
	const [tried, setTried] = useState<boolean>(false);
	const reasonRef = useRef<HTMLTextAreaElement>(null);

	const survivor = members.find((member) => member.id === survivorId) || members[0];
	const survivorHasSizes = Number(survivor?.variantCount || 0) > 0;

	useEffect(() => {
		setVariantFor({});
		setSurvivorVariants([]);
		if (!survivor || !survivorHasSizes) return;
		let alive = true;
		http.get(campusId, survivor.id)
			.then((response) => { if (alive) setSurvivorVariants((response.data.variants || []).filter((variant: any) => !variant.deprecatedAt)); })
			.catch((requestError) => { if (alive) setError(apiError(requestError, "No se pudieron cargar las tallas de «" + survivor.name + "».")); });
		return () => { alive = false; };
	}, [survivorId]);

	const needsVariant = (member: Seleccionado) => survivorHasSizes && !(Number(member.variantCount || 0) > 0);
	const pairs = survivor ? members.filter((member) => member.id !== survivor.id)
		.map((member) => ({ keeperId: needsVariant(member) ? Number(variantFor[member.id]) || 0 : survivor.id, mergedId: member.id })) : [];
	const complete = pairs.length > 0 && pairs.every((pair) => pair.keeperId > 0);
	const pairsKey = JSON.stringify(pairs);

	useEffect(() => {
		setPreview(null);
		setError(null);
		if (!complete) { setLoading(false); return; }
		let alive = true;
		setLoading(true);
		const timer = window.setTimeout(() => {
			http.mergePreview(campusId, pairs)
				.then((response) => { if (alive) setPreview(response.data); })
				.catch((requestError) => { if (alive) setError(apiError(requestError, "No se pudo revisar la unificación.")); })
				.finally(() => { if (alive) setLoading(false); });
		}, 250);
		return () => { alive = false; window.clearTimeout(timer); setLoading(false); };
	}, [campusId, pairsKey]);

	const run = async () => {
		setBusy(true);
		setError(null);
		try {
			const response = await http.merge(campusId, pairs, reason.trim());
			onDone(response.data?.message || "Listo.", Number(response.data?.survivorId) || Number(survivor?.modelId) || survivor.id);
		} catch (requestError) {
			setError(apiError(requestError, "No se pudo unificar."));
		} finally {
			setBusy(false);
		}
	};

	const addMember = (picked: ProductRow) => {
		if (members.some((member) => member.id === picked.id)) return;
		setMembers([...members, { id: picked.id, name: picked.name, waCode: picked.waCode, kind: picked.kind, onHand: Number(picked.onHand || 0), variantCount: Number(picked.variantCount || 0), modelId: picked.modelId || null }]);
	};
	const removeMember = (id: number) => {
		const rest = members.filter((member) => member.id !== id);
		setMembers(rest);
		if (survivorId === id && rest.length) setSurvivorId(rest[0].id);
	};

	const blockers: string[] = preview?.blockers || [];
	const missingSize = members.find((member) => member.id !== survivor?.id && needsVariant(member) && !(Number(variantFor[member.id]) > 0));
	const reasonMissing = reason.trim().length < 3;
	/** Por qué todavía no se puede unificar: se dice junto al botón, al pasar el mouse y al tocarlo. */
	const whyNot: string | null = busy ? null
		: pairs.length < 1 ? "Agrega al menos otro producto repetido."
		: missingSize ? "Elige qué talla es «" + missingSize.name + "»."
		: loading ? "Revisando qué pasará…"
		: preview === null ? (error || "Revisando qué pasará…")
		: blockers.length ? "No se puede todavía: mira lo marcado arriba."
		: reasonMissing ? "Escribe el motivo."
		: null;
	const tryRun = () => {
		if (busy) return;
		if (whyNot) {
			setTried(true);
			if (preview && !blockers.length && reasonMissing) reasonRef.current?.focus();
			return;
		}
		run();
	};
	const kinds = members.some((member) => COMPOSITE.includes(String(member.kind))) ? COMPOSITE : SIMPLE_KINDS;

	const pie = <>
		{whyNot ? <span className="wti-porque" role="status">{whyNot}</span> : null}
		<button type="button" className="wau-btn s" disabled={busy} onClick={onClose}>Cancelar</button>
		<button type="button" className={"wau-btn p" + (whyNot ? " wti-apagado" : "")} disabled={busy} title={whyNot || undefined} aria-disabled={!!whyNot}
			onClick={tryRun}>{busy ? "Unificando…" : pairs.length === 1 ? "Unificar" : "Unificar " + (pairs.length + 1) + " Productos"}</button>
	</>;

	return <VentanaTienda titulo="Unificar Repetidos" onHide={onClose} busy={busy} pie={pie} sobre={sobre}>
		<div className="wau-lab" style={{ marginBottom: 6 }}>¿Cuál se queda?</div>
		<div style={{ border: "1px solid var(--wa-filo)", borderRadius: 10, overflow: "hidden" }}>
			{members.map((member) => {
				const isSurvivor = member.id === survivor?.id;
				return <div key={member.id} className={"wti-miembro" + (isSurvivor ? " queda" : "")} onClick={() => { if (!busy) setSurvivorId(member.id); }}>
					<input type="radio" name="wtiSobrevive" checked={isSurvivor} disabled={busy} onChange={() => setSurvivorId(member.id)} aria-label={"Se queda " + member.name} />
					<div style={{ flex: "1 1 200px", minWidth: 0 }}>
						<div style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{member.name}</div>
						<div className="wti-sub">{member.waCode || "sin código"} · {Number(member.variantCount || 0) > 0 ? member.variantCount + " tallas o colores" : "existencia " + Number(member.onHand || 0)}</div>
					</div>
					{isSurvivor ? <span className="wti-queda">SE QUEDA</span>
						: needsVariant(member) ? <select className={"wau-entrada" + (tried && !(Number(variantFor[member.id]) > 0) ? " falta" : "")} style={{ width: 180 }} value={variantFor[member.id] || ""} disabled={busy}
							onClick={(event) => event.stopPropagation()} onChange={(event) => setVariantFor({ ...variantFor, [member.id]: event.target.value })}>
							<option value="">¿Qué talla es?</option>
							{survivorVariants.map((variant) => <option key={variant.id} value={variant.id}>{variant.label || variant.sizeValue}</option>)}
						</select> : null}
					{members.length > 2 ? <button type="button" className="wti-link" disabled={busy} onClick={(event) => { event.stopPropagation(); removeMember(member.id); }}>Quitar</button> : null}
				</div>;
			})}
		</div>
		<div style={{ marginTop: 10 }}>
			<ProductPicker campusId={campusId} kinds={kinds} disabled={busy} placeholder="Agregar otro repetido…" excludeIds={members.map((member) => member.id)} onPick={addMember} />
		</div>

		{pairs.length > 0 && !complete ? <div className="wti-sub" style={{ marginTop: 10 }}>Elige la talla de cada uno para ver qué pasará.</div> : null}
		{loading ? <Loading /> : preview ? <>
			{blockers.length ? <div className="wau-nota" style={{ marginTop: 12 }}>
				<b>Todavía no se puede</b>
				{blockers.map((text, index) => <span key={index}>{text}</span>)}
			</div> : <>
				<div className="wau-lab" style={{ marginTop: 14 }}>Qué va a pasar</div>
				{(preview.items || []).map((item: any, index: number) => <div key={index} className="wti-par">
					<b>{item.merged.name}</b> <span className="wti-sub">→ {item.keeper.name}</span>
					<ul>
						{(item.summary || []).slice(0, -1).map((text: string, line: number) => <li key={line}>{text}</li>)}
						{(item.warnings || []).map((text: string, line: number) => <li key={"w" + line} className="wau-oro">{text}</li>)}
					</ul>
				</div>)}
				<div className="wau-campo" style={{ marginTop: 14 }}>
					<span className="wau-lab">Motivo <span className="wau-oro">*</span></span>
					<textarea ref={reasonRef} className={"wau-entrada" + (tried && reasonMissing ? " falta" : "")} rows={2} style={{ resize: "vertical" }} value={reason} disabled={busy} maxLength={500}
						placeholder="Ej.: se dio de alta varias veces en el conteo" onChange={(event) => setReason(event.target.value)} />
					{tried && reasonMissing ? <div className="wti-falta-texto">Escribe por qué se unifican (mínimo 3 letras).</div> : null}
				</div>
				<div className="wti-sub" style={{ marginTop: 6 }}>Los repetidos quedan ocultos con su historial. Se puede deshacer.</div>
			</>}
		</> : null}
		{error ? <div className="wau-nota" style={{ marginTop: 10 }}>{error}</div> : null}
	</VentanaTienda>;
};

interface HistoryProps {
	campusId: number;
	productId: number;
	/** Cambia cuando algo cambió, para volver a pedir la lista. */
	refreshKey: number;
	onUndone: (message: string) => void;
}

/** Unificaciones donde está este producto, con su botón Deshacer. No se ve nada si no hay. */
export const UnificacionesHistorial: React.FC<HistoryProps> = ({ campusId, productId, refreshKey, onUndone }) => {
	const http = HttpManager.getInstance().inventoryService;
	const [rows, setRows] = useState<any[]>([]);
	const [undoing, setUndoing] = useState<number | null>(null);
	const [reason, setReason] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const request = useRef<number>(0);

	const load = useCallback(() => {
		// Solo la última respuesta pinta.
		const mine = ++request.current;
		http.mergeHistory(campusId, productId)
			.then((response) => { if (mine === request.current) setRows(response.data || []); })
			.catch(() => { if (mine === request.current) setRows([]); });
	}, [campusId, productId]);
	useEffect(() => { load(); }, [load, refreshKey]);

	const undo = async (mergeId: number) => {
		setBusy(true);
		setError(null);
		try {
			const response = await http.undoMerge(campusId, mergeId, reason.trim());
			setUndoing(null);
			setReason("");
			load();
			onUndone(response.data?.message || "Se deshizo.");
		} catch (requestError) {
			setError(apiError(requestError, "No se pudo deshacer."));
		} finally {
			setBusy(false);
		}
	};

	if (rows.length < 1) return null;
	return <div style={{ marginTop: 16 }}>
		<div style={{ fontSize: 12, color: "var(--wa-apagado)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>Unificaciones</div>
		<ul className="wti-lista-simple">{rows.map((row) => <li key={row.id} style={{ opacity: row.undoneAt ? 0.6 : 1 }}>
			<div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
				<div style={{ flex: "1 1 260px", minWidth: 0 }}>
					{row.mergedCount > 1 ? <>{row.mergedCount} productos unidos a <b>{row.keeperName}</b></> : <><b>{row.mergedName}</b> unido a <b>{row.keeperName}</b></>}
					<div className="wti-sub">{row.at} · {row.by} · {row.reason}</div>
					{row.undoneAt ? <div className="wti-sub wau-oro">Deshecha el {row.undoneAt} por {row.undoneBy}</div> : null}
					{row.blockedBy ? <div className="wti-sub">Para deshacerla, primero deshaz la #{row.blockedBy.id}.</div> : null}
				</div>
				{!row.undoneAt && !row.blockedBy && undoing !== row.id
					? <button type="button" className="wau-btn s mini" disabled={busy} onClick={() => { setUndoing(row.id); setReason(""); setError(null); }}>Deshacer</button> : null}
			</div>
			{undoing === row.id ? <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
				<input className={"wau-entrada" + (error && reason.trim().length < 3 ? " falta" : "")} style={{ flex: "1 1 220px" }} autoFocus maxLength={500} placeholder="Por qué se deshace" value={reason} disabled={busy}
					onChange={(event) => setReason(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && reason.trim().length >= 3) undo(row.id); }} />
				<button type="button" className={"wau-btn p mini" + (reason.trim().length < 3 ? " wti-apagado" : "")} disabled={busy}
					title={reason.trim().length < 3 ? "Escribe por qué se deshace." : undefined}
					onClick={() => { if (reason.trim().length < 3) { setError("Escribe por qué se deshace (mínimo 3 letras)."); return; } undo(row.id); }}>{busy ? "Deshaciendo…" : "Deshacer"}</button>
				<button type="button" className="wau-btn s mini" disabled={busy} onClick={() => setUndoing(null)}>Cancelar</button>
			</div> : null}
			{undoing === row.id && error ? <div className="wau-nota" style={{ marginTop: 6 }}>{error}</div> : null}
		</li>)}</ul>
	</div>;
};
